// hooks/useSwipeReply.js
//
// Unified gesture handler for the message row (outer div).
// Handles THREE competing gestures in priority order:
//
//   1. SCROLL   — vertical movement → immediately yield to native scroll
//   2. LONG-PRESS → selection mode (350 ms hold with no movement)
//   3. SWIPE    — horizontal right movement → reply animation + callback
//
// Key design decisions:
//   - A SINGLE touchstart/move/end on the OUTER ROW handles everything.
//     The inner bubble has NO touch handlers (removed from MessageList).
//   - Direction is locked within the first 6 px of movement.
//     Before lock: long-press timer still runs, no translation applied.
//     After vertical lock: timer cancelled, native scroll takes over (no preventDefault).
//     After horizontal lock: preventDefault() called to block scroll, swipe begins.
//   - The reply fires ONCE at the 60px threshold while still dragging (WhatsApp style).
//   - Multi-touch safe: gestures keyed by touch.identifier.
//   - Works identically on iOS Safari, Android Chrome, and tablet.

import { useRef, useCallback } from "react";

/* ─── Tuning ────────────────────────────────────────────────────────────── */
const DIRECTION_LOCK_PX    = 6;    // movement before we decide H vs V
const DIRECTION_LOCK_RATIO = 1.2;  // dx must be this × dy to count as horizontal
const TRIGGER_THRESHOLD    = 60;   // px — fires reply + haptic
const HARD_MAX             = 80;   // px — rubber-band ceiling
const DAMPING              = 0.42; // resistance beyond TRIGGER_THRESHOLD
const LONG_PRESS_MS        = 350;  // ms — selection mode threshold
const SCROLL_ABORT_PX      = 8;    // vertical movement that kills long-press

const SPRING   = "cubic-bezier(0.34, 1.56, 0.64, 1)";
const SNAP     = "cubic-bezier(0.25, 0.46, 0.45, 0.94)";

/* ─── helpers ────────────────────────────────────────────────────────────── */
function rubberband(x) {
  if (x <= 0) return 0;
  if (x <= TRIGGER_THRESHOLD) return x;
  return TRIGGER_THRESHOLD + (x - TRIGGER_THRESHOLD) * DAMPING;
}

function iconProgress(x) {
  const start = TRIGGER_THRESHOLD * 0.3;
  const range = TRIGGER_THRESHOLD - start;
  if (x <= start) return 0;
  return Math.min((x - start) / range, 1);
}

function getBubbleEl(rowEl) {
  // First non-system child that carries willChange:transform (the bubble wrapper)
  return rowEl?.querySelector?.("[data-bubble]") ?? null;
}

function getIconEl(rowEl) {
  return rowEl?.querySelector?.("[data-swipe-icon]") ?? null;
}

function setIconProgress(iconEl, progress) {
  if (!iconEl) return;
  iconEl.style.opacity   = String(progress);
  iconEl.style.transform = `translateY(-50%) scale(${0.4 + progress * 0.6})`;
}

function applyTranslate(bubbleEl, x, animated, easing = SPRING) {
  if (!bubbleEl) return;
  bubbleEl.style.transition = animated ? `transform 0.35s ${easing}` : "none";
  bubbleEl.style.transform  = x === 0 ? "" : `translateX(${x}px)`;
}

/* ════════════════════════════════════════════════════════════════════════════
   useSwipeReply
   Returns: { onRowTouchStart, onRowTouchMove, onRowTouchEnd, onRowTouchCancel }
   These go on the OUTER MESSAGE ROW div.
   The inner bubble needs data-bubble attribute so we can find it.
════════════════════════════════════════════════════════════════════════════ */
export function useSwipeReply({
  setSelectedReply,
  uuid,
  // Selection callbacks (replaces useMessageSelection touch handlers)
  onLongPress,    // (msgId) => void  — called after LONG_PRESS_MS
  onTap,          // (msgId) => void  — short tap in selection mode
  selectionMode,  // boolean — whether selection is currently active
}) {
  const gestureMap = useRef(new Map());

  const onRowTouchStart = useCallback((e, message, msgId) => {
    if (e.touches.length > 1) return; // ignore multi-touch
    const touch = e.touches[0];
    const id    = touch.identifier;
    if (gestureMap.current.has(id)) return;

    const rowEl    = e.currentTarget;
    const bubbleEl = getBubbleEl(rowEl);
    const iconEl   = getIconEl(rowEl);

    // Cancel any lingering animation on this bubble
    if (bubbleEl) {
      bubbleEl.style.transition = "none";
      bubbleEl.style.transform  = "";
    }

    // Start long-press timer
    const timer = setTimeout(() => {
      const g = gestureMap.current.get(id);
      if (!g || g.aborted || g.direction === "vertical") return;
      g.longPressFired = true;
      try { navigator?.vibrate?.(10); } catch {}
      onLongPress?.(msgId);
    }, LONG_PRESS_MS);

    gestureMap.current.set(id, {
      startX:        touch.clientX,
      startY:        touch.clientY,
      direction:     null,   // null | "horizontal" | "vertical"
      aborted:       false,
      replyFired:    false,
      longPressFired: false,
      timer,
      rowEl,
      bubbleEl,
      iconEl,
      message,
      msgId,
    });
  }, [onLongPress]);

  const onRowTouchMove = useCallback((e) => {
    const touch = e.touches[0];
    const g     = gestureMap.current.get(touch.identifier);
    if (!g || g.aborted) return;

    const dx = touch.clientX - g.startX;
    const dy = touch.clientY - g.startY;
    const absDx = Math.abs(dx);
    const absDy = Math.abs(dy);

    /* ── Direction detection ── */
    if (!g.direction) {
      const moved = Math.sqrt(dx * dx + dy * dy);
      if (moved < DIRECTION_LOCK_PX) return; // not enough movement yet

      if (absDy > SCROLL_ABORT_PX || absDy * DIRECTION_LOCK_RATIO > absDx) {
        // Vertical / diagonal → scroll wins, kill long-press
        g.direction = "vertical";
        clearTimeout(g.timer);
        return;
      }

      // Horizontal swipe confirmed
      g.direction = "horizontal";
      clearTimeout(g.timer); // long-press can't fire once we're swiping
    }

    if (g.direction === "vertical") return;

    // ── Horizontal swipe ──
    // Prevent scroll from competing now that we've locked horizontally
    e.preventDefault();

    if (dx <= 0) {
      // Left swipe or back — reset
      applyTranslate(g.bubbleEl, 0, false);
      setIconProgress(g.iconEl, 0);
      return;
    }

    const visual   = rubberband(dx);
    const progress = iconProgress(dx);
    applyTranslate(g.bubbleEl, Math.min(visual, HARD_MAX), false);
    setIconProgress(g.iconEl, progress);

    // Fire reply once at threshold
    if (dx >= TRIGGER_THRESHOLD && !g.replyFired) {
      g.replyFired = true;
      try { navigator?.vibrate?.(10); } catch {}
    }
  }, []);

  const onRowTouchEnd = useCallback((e) => {
    const touch = e.changedTouches[0];
    const g     = gestureMap.current.get(touch.identifier);
    gestureMap.current.delete(touch.identifier);
    if (!g) return;

    clearTimeout(g.timer);

    /* ── Long-press already fired → nothing extra for selection ── */
    if (g.longPressFired) return;

    /* ── Short tap while selection mode active ── */
    if (g.direction === null && !g.longPressFired) {
      if (selectionMode) {
        onTap?.(g.msgId);
      }
    }

    /* ── Swipe end ── */
    if (g.direction === "horizontal") {
      const dx = touch.clientX - g.startX;

      if (g.replyFired) {
        // Kick animation
        const kickX = Math.min(rubberband(dx), HARD_MAX) + 8;
        if (g.bubbleEl) {
          g.bubbleEl.style.transition = "transform 0.12s ease-out";
          g.bubbleEl.style.transform  = `translateX(${kickX}px)`;
          setTimeout(() => {
            if (!g.bubbleEl) return;
            g.bubbleEl.style.transition = `transform 0.35s ${SNAP}`;
            g.bubbleEl.style.transform  = "";
          }, 120);
        }
        if (g.iconEl) {
          g.iconEl.style.transition = "opacity 0.25s ease, transform 0.25s ease";
          setIconProgress(g.iconEl, 0);
        }

        // Fire reply
        setSelectedReply({
     msgId:      g.message.messageId || g.message.id || g.message.raw?.key?.msgId || uuid(),
          text:       g.message.text      || g.message.raw?.message?.conversation || "",
          media:      g.message.media     || g.message.raw?.message?.imageMessage
                      || g.message.raw?.message?.videoMessage || null,
          senderName: g.message.senderName || g.message.username || null,
        });
      } else {
        // Below threshold — snap back
        if (g.bubbleEl) {
          g.bubbleEl.style.transition = `transform 0.28s ${SNAP}`;
          g.bubbleEl.style.transform  = "";
        }
        if (g.iconEl) {
          g.iconEl.style.transition = "opacity 0.2s ease, transform 0.2s ease";
          setIconProgress(g.iconEl, 0);
        }
      }
    }
  }, [setSelectedReply, uuid, selectionMode, onTap]);

  const onRowTouchCancel = useCallback((e) => {
    const touch = e.changedTouches[0];
    const g     = gestureMap.current.get(touch.identifier);
    gestureMap.current.delete(touch.identifier);
    if (!g) return;

    clearTimeout(g.timer);

    if (g.bubbleEl) {
      g.bubbleEl.style.transition = `transform 0.22s ${SNAP}`;
      g.bubbleEl.style.transform  = "";
    }
    if (g.iconEl) {
      g.iconEl.style.transition = "opacity 0.18s ease, transform 0.18s ease";
      setIconProgress(g.iconEl, 0);
    }
  }, []);

  return { onRowTouchStart, onRowTouchMove, onRowTouchEnd, onRowTouchCancel };
}